import React, { useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useParams } from 'react-router-dom';

import { Button, Stack, Typography } from '@mui/material';

import { API } from '@luxuryescapes/lib-types';

import SalesforceBlackoutDatesWizardModal from '~/components/Offers/Detail/OfferBuilder/DealInformation/tabs/blackoutDates/SalesforceBlackoutDatesWizardModal';
import BlackoutDates from '~/components/Vendors/Properties/BlackoutDates';

import { useGetOffer, useGetOpportunity } from '~/hooks/apis/offers/useGetOffer';
import { roomRateAvailabilityQueryKeys } from '~/hooks/apis/reservation/roomRateAvailabilityQueryKeys';
import { useGetRoomRateBlackoutDates } from '~/hooks/apis/reservation/useGetRoomRateBlackoutDates';

import ReservationService from '~/services/ReservationService';

import { deleteAllInChunks } from '~/utils/deleteAllInChunks';

interface Props {
  propertyId: string;
  roomType: App.RoomType;
  roomRate: App.RoomRate;
  propertyRoomTypes: Array<App.RoomType>;
  channelManaged: boolean;
}

export default function BlackoutsTab({ propertyId, roomType, roomRate, propertyRoomTypes, channelManaged }: Props) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [isDeletingAll, setIsDeletingAll] = useState(false);
  const [isImportBlackoutDatesModalOpen, setIsImportBlackoutDatesModalOpen] = useState(false);
  const { id_offer: offerId } = useParams<{ id_offer: string }>();
  const { data: offer } = useGetOffer(offerId);
  const opportunityId = offer?.id_salesforce_external ?? offerId;
  const { data: opportunity } = useGetOpportunity(opportunityId, { enabled: !!opportunityId });
  const { data: blackoutDates = [], refetch } = useGetRoomRateBlackoutDates(propertyId, roomType.id, roomRate.id);

  function invalidate() {
    queryClient.invalidateQueries({
      queryKey: roomRateAvailabilityQueryKeys.blackout(propertyId, roomType.id, roomRate.id),
    });
  }

  async function handleDeleteAll() {
    setIsDeletingAll(true);
    const ids = blackoutDates.map((blackoutDate) => blackoutDate.id);
    await deleteAllInChunks(
      ids,
      (id) => ReservationService.deleteBlackoutDateBlock(id, propertyId, roomType.id, roomRate.id),
      (error) => enqueueSnackbar(`Failed to delete all blackout dates: ${error.message}`, { variant: 'error' }),
    );
    setIsDeletingAll(false);
    invalidate();
  }

  const canImportBlackoutDates = !!offer && !!offerId && !!offer.vendor_account_id;

  return (
    <>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Dates blocked from sale for this room rate.
        </Typography>
        <Button
          variant="contained"
          disabled={!canImportBlackoutDates}
          onClick={() => setIsImportBlackoutDatesModalOpen(true)}
        >
          Add Salesforce blackout dates
        </Button>
      </Stack>

      {isImportBlackoutDatesModalOpen && canImportBlackoutDates && (
        <SalesforceBlackoutDatesWizardModal
          blackoutDates={opportunity?.black_out_dates ?? []}
          blackoutDatesByRoomRate={{ [roomRate.id]: blackoutDates }}
          onClose={() => {
            setIsImportBlackoutDatesModalOpen(false);
            refetch();
          }}
          propertyId={propertyId}
          roomRateSelections={[{ roomType, roomRate }]}
          vendorId={offer.vendor_account_id}
          lockRoomRates
        />
      )}

      <BlackoutDates
        propertyId={propertyId}
        channelManaged={channelManaged}
        roomType={roomType as unknown as API.Reservation.RoomType}
        roomRate={roomRate as unknown as API.Reservation.RoomRate}
        propertyRoomTypes={propertyRoomTypes}
        blackoutDates={blackoutDates}
        isDeletingAll={isDeletingAll}
        deleteBlackoutDateBlock={async (id: string) => {
          await ReservationService.deleteBlackoutDateBlock(id, propertyId, roomType.id, roomRate.id);
          refetch();
        }}
        onUpdateBlackoutDates={() => refetch()}
        onDeleteAll={handleDeleteAll}
      />
    </>
  );
}
